import { MDBBadge, MDBBtn, MDBContainer, MDBTable, MDBTableBody, MDBTableHead, } from "mdb-react-ui-kit";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDarkMode } from "../../components/assets/DarkModeContext";
import UserSearch from "../../components/assets/UserSearch";
import { useCurrentUser } from "../../components/hooks/useCurrentUser";
import useDebounce from "../../components/hooks/useDebounce";
import OriginalResumePdfPreview from "../../components/resume-list/OriginalResumePdfPreview";
import { downloadUserPdf, getAllUsers, getAllUsersWithResumes, } from "../../components/services/httpService";

export default function Candidats() {
    const [ users, setUsers ] = useState( [] );
    const [ usersWithResumes, setUsersWithResumes ] = useState( [] );
    const [ search, setSearch ] = useState( "" );
    const [ activeTab, setActiveTab ] = useState( "cvGeneres" );
    const [ suggestions, setSuggestions ] = useState( [] );
    const [ allUsers, setAllUsers ] = useState( [] );
    const [ filteredUsers, setFilteredUsers ] = useState( [] );
    const [ filteredUser, setFilteredUser ] = useState( null );
    const { isDarkMode, isTransitionDisabled } = useDarkMode();
    const currentUser = useCurrentUser();
    const noTransitionClass = isTransitionDisabled ? "no-transition" : "";
    const debouncedSearchTerm = useDebounce( search, 300 );

    const candidates = useMemo( () => {
        return usersWithResumes.filter(
            ( user ) => user.role === "CANDIDAT" || user.role === "MANAGER" || user.role === "CONSULTANT"
        );
    }, [ usersWithResumes ] );

    const [ activeSuggestionIndex, setActiveSuggestionIndex ] = useState( -1 );
    const history = useHistory();

    useEffect( () => {
        const filtered = candidates.filter( ( user ) => {
            const userFullName = `${ user.prenom } ${ user.nom }`.toLowerCase();
            const searchWords = search.toLowerCase().split( /\s+/ );
            return searchWords.every( ( word ) => userFullName.includes( word ) );
        } );

        setFilteredUsers( filtered );
    }, [ candidates, search ] );

    useEffect( () => {
        if ( debouncedSearchTerm ) {
            const searchWords = debouncedSearchTerm.toLowerCase().split( /\s+/ );

            const filteredSuggestions = allUsers.filter( ( user ) => {
                const userFullName =
                    user.prenom?.toLowerCase() + " " + user.nom?.toLowerCase();
                return searchWords.every( ( word ) => userFullName.includes( word ) );
            } );

            setSuggestions( filteredSuggestions );
        } else {
            setSuggestions( [] );
        }
    }, [ debouncedSearchTerm, allUsers ] );

    const candidatesPdf = users.filter(
        ( user ) => user.role === "CANDIDAT" || user.role === "MANAGER" || user.role === "CONSULTANT"
    );

    const filteredUsersSecondTable = candidatesPdf.filter( ( user ) => {
        const userFullName = `${ user.prenom } ${ user.nom }`.toLowerCase();
        const searchWords = search.toLowerCase().split( /\s+/ );
        return searchWords.every( ( word ) => userFullName.includes( word ) );
    } );

    const getLatestModificationDate = ( user ) => {
        if ( !user.resumes || user.resumes.length === 0 ) {
            return new Date( 0 );
        } // retorna una fecha antigua si no hay CVs

        return new Date(
            Math.max( ...user.resumes.map( ( resume ) => new Date( resume.lastModificationDate
                ? resume.lastModificationDate : resume.creationDate )
            ) )
        );
    };

    const sortedFilteredUsers = [ ...filteredUsers ].sort(
        ( a, b ) => getLatestModificationDate( b ) - getLatestModificationDate( a )
    );

    // Liste de toutes les CVs existantes de la bdd
    useEffect( () => {
        getAllUsersWithResumes()
            .then( ( response ) => {
                if ( !response.ok ) {
                    throw new Error( "Network response was not ok" );
                }
                return response.json();
            } )
            .then( ( data ) => {
                setUsersWithResumes( data );
            } )
            .catch( ( error ) => {
                console.error(
                    "Erreur lors de la récupération des utilisateurs et de leurs CVs:",
                    error
                );
            } );
        const handleKeyDown = ( event ) => {
            if ( event.key === "Enter" ) {
                event.preventDefault();
            }
        };

        // Ajoute l'écouteur d'événements lorsque le composant est monté
        document.addEventListener( "keydown", handleKeyDown );

        // Supprime l'écouteur lorsque le composant est démonté
        return () => {
            document.removeEventListener( "keydown", handleKeyDown );
        };
    }, [] );

    //Sauvegarder le cv en PDF récuperé de la liste UserSearch dans cv d'origine
    useEffect( () => {
        const fetchUsersWithPdf = async () => {
            const responseUsers = await getAllUsers();
            const dataUsers = await responseUsers.json();

            const usersWithPdf = await Promise.all(
                dataUsers.map( async ( user ) => {
                    if ( !user.docUrl ) {
                        return user;
                    }

                    const responsePdf = await downloadUserPdf( user.id );

                    if ( !responsePdf.ok ) {
                        return user;
                    }

                    const pdfBlob = await responsePdf.blob();
                    const pdfUrl = URL.createObjectURL( pdfBlob );
                    return { ...user, pdfUrl };
                } )
            );

            setUsers( usersWithPdf );
            setUsersWithResumes( usersWithPdf );
        };

        fetchUsersWithPdf();
    }, [] );

    const handleTabChange = ( tab ) => {
        setActiveTab( tab );
    };

    const handleUserSearch = async ( searchQuery ) => {
        if ( searchQuery === "" ) {
            setSuggestions( [] );
            setFilteredUser( { prenom: "", nom: "", role: "" } );
            return;
        }

        if ( allUsers.length === 0 ) {
            const response = await getAllUsers();
            const data = await response.json();
            setAllUsers( data );
        }

        const matchingUsers = allUsers.filter(
            ( u ) =>
                u.prenom?.toLowerCase().startsWith( searchQuery.toLowerCase() ) ||
                u.nom?.toLowerCase().startsWith( searchQuery.toLowerCase() )
        );

        setSuggestions( matchingUsers );

        if ( matchingUsers.length === 1 ) {
            setFilteredUser( matchingUsers[ 0 ] );
        } else {
            setFilteredUser( { prenom: "", nom: "", role: "" } );
        }
    };

    const handleOnSearch = ( searchQuery ) => {
        setSearch( searchQuery );
        handleUserSearch( searchQuery );
    };

    const updateActiveSuggestionIndex = ( newIndex ) => {
        setActiveSuggestionIndex( newIndex );
    };

    const handleSuggestionClick = ( user ) => {
        setFilteredUser( user );
        setSuggestions( [] );
    };

    const handleSuggestionClickCv = ( user ) => {
        setFilteredUser( user );
        setSuggestions( [] );
        history.push( `/user-cvs/${ user.id }` );
    };

    const handleSelection = () => {
        if (
            activeSuggestionIndex >= 0 &&
            activeSuggestionIndex < suggestions.length
        ) {
            handleSuggestionClick( suggestions[ activeSuggestionIndex ] );
        }
    };

    function formatPrenom( prenom ) {
        if ( !prenom ) return "";

        return prenom
            .split( /[-\s]/ )
            .map( ( word ) => word.charAt( 0 ).toUpperCase() + word.slice( 1 ).toLowerCase() )
            .join( prenom.includes( "-" ) ? "-" : " " );
    }

    const [ totalNumberOfResumes, setTotalNumberOfResumes ] = useState( 0 );

    useEffect( () => {
        const total = sortedFilteredUsers.reduce( ( acc, user ) => acc + user.resumes.length, 0 );
        setTotalNumberOfResumes( total );
    }, [ sortedFilteredUsers ] );
    return (
        <MDBContainer fluid className={ isDarkMode ? "bg-dark-mode" : "bg-light" }>
            <MDBContainer>
                <section className="animation-fade-in">
                    <div
                        className="bg-image"
                        style={ {
                            height: "300px",
                            backgroundImage: 'url("./bg/bg-mes-cv.png")',
                            backgroundSize: "cover",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "center",
                            backgroundAttachment: "fixed",
                        } }
                    ></div>
                </section>
                <div className="d-flex text-center justify-content-center align-items-center my-3">
                    <Link to="/inscription-candidat">
                        <MDBBtn className="BtnCard">Inscrire un candidat</MDBBtn>
                    </Link>
                </div>
                <section>
                    <h1 className={ `text-center ${ isDarkMode ? "text-white" : "" }` }>
                        CVs Candidats
                    </h1>
                    <div className="search-container w-50 m-auto">
                        <UserSearch
                            onSearch={ handleOnSearch }
                            activeSuggestionIndex={ activeSuggestionIndex }
                            updateActiveSuggestionIndex={ setActiveSuggestionIndex }
                            suggestionsCount={ suggestions.length }
                            onSelection={ handleSelection }
                            suggestions={ suggestions }
                            placeholder={ "Choisissez un candidat pour créer un CV" }
                        />
                        { ( suggestions.length > 0 || ( search.length > 0 && sortedFilteredUsers.length === 0 ) ) && (
                            <div className="suggestions-box m-auto">
                                { suggestions.length === 0 ? (
                                    <div className="suggestion">Aucun Résultat</div>
                                ) : (
                                    suggestions.map( ( user, idx ) => (
                                        <div
                                            key={ idx }
                                            className={ `suggestion ${ activeSuggestionIndex === idx ? "active" : "" }` }
                                            onClick={ () => handleSuggestionClickCv( user ) }
                                            onMouseEnter={ () => setActiveSuggestionIndex( idx ) }
                                        >
                                            <strong>{ user.nom.toUpperCase() }</strong>{ " " }
                                            { ` ${ formatPrenom( user.prenom ) }` }
                                        </div>
                                    ) )
                                ) }
                            </div>
                        ) }
                    </div>
                    <MDBContainer className="pb-5">
                        <div>
                            <ul
                                className="nav nav-tabs mb-5 justify-content-center"
                                id="issuesTabs"
                                role="tablist"
                            >
                                <li
                                    className={ `nav-item ${ noTransitionClass }` }
                                    role="presentation"
                                >
                                    <button
                                        className={ `nav-link ${ activeTab === "cvGeneres" ? "active" : ""
                                        } ${ isDarkMode ? "text-white bg-dark-mode" : "" }` }
                                        id="tab"
                                        onClick={ () => handleTabChange( "cvGeneres" ) }
                                    >
                                        CV générés
                                    </button>
                                </li>
                                <li
                                    className={ `nav-item ${ noTransitionClass }` }
                                    role="presentation"
                                >
                                    <button
                                        className={ `nav-link ${ activeTab === "cvOrigine" ? "active" : ""
                                        } ${ isDarkMode ? "text-white bg-dark-mode" : "" }` }
                                        id="tab2"
                                        onClick={ () => handleTabChange( "cvOrigine" ) }
                                    >
                                        CV d'origine
                                    </button>
                                </li>
                                <li
                                    className={ `nav-item ${ noTransitionClass }` }
                                    role="presentation"
                                >
                                    <button
                                        className={ `nav-link ${ activeTab === "testsTechniques" ? "active" : ""
                                        } ${ isDarkMode ? "text-white bg-dark-mode" : "" }` }
                                        id="tab3"
                                        onClick={ () => handleTabChange( "testsTechniques" ) }
                                    >
                                        Tests Techniques
                                    </button>
                                </li>
                            </ul>
                        </div>
                        { activeTab === "cvGeneres" && (
                            <div>
                                <div className="text-center">
                                    <MDBBadge className="mx-2 mb-2" color="info">
                                        Nombre total de CVs : { totalNumberOfResumes }
                                    </MDBBadge>
                                </div>
                                <div
                                    className="shadow-4 rounded-5 table-container"
                                    style={ { overflowX: "auto" } }
                                >
                                    <MDBTable>
                                        <MDBTableHead
                                            className={ `${ isDarkMode ? "bg-dark-mode" : "" }` }
                                        >
                                            <tr style={ { color: "#09B4BF" } }>
                                                <th>Nom et prénom</th>
                                                <th>Date de inscription</th>
                                                <th>Date de dernière de modification</th>
                                                <th>CVs générés</th>
                                            </tr>
                                        </MDBTableHead>
                                        <MDBTableBody
                                            style={ { verticalAlign: "middle" } }
                                            className={ isDarkMode ? "text-white" : "" }
                                        >
                                            { sortedFilteredUsers && sortedFilteredUsers.map( ( user ) => (
                                                <tr
                                                    key={ user.id }
                                                    className={ isDarkMode ? "text-white" : "text-muted" }
                                                >
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <img
                                                                src="/spinner-alcyone.png"
                                                                alt=""
                                                                style={ { width: "45px", height: "45px" } }
                                                                className="rounded-circle"
                                                            />
                                                            <Link
                                                                to={ `/user-cvs/${ user.id }` }
                                                                className="d-flex align-items-center ms-2"
                                                            >
                                                                <p className="noLinkStyle fw-bold mb-1">
                                                                  <span className="text-uppercase">
                                                                    { user.nom }
                                                                  </span>{ " " }
                                                                    { formatPrenom( user.prenom ) }
                                                                </p>
                                                            </Link>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            { new Date( user.dateCreation ).toLocaleDateString(
                                                                "fr-FR"
                                                            ) }
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="ms-4">
                                                            { user.resumes &&
                                                            user.resumes[ 0 ] &&
                                                            user.resumes[ 0 ].lastModificationDate
                                                                ? new Date(
                                                                    user.resumes[ 0 ].lastModificationDate
                                                                ).toLocaleDateString( "fr-FR" )
                                                                : "-" }
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="ms-4">{ user.resumes.length }</div>
                                                    </td>
                                                </tr>
                                            ) ) }
                                        </MDBTableBody>
                                    </MDBTable>
                                </div>
                            </div>
                        ) }
                        { activeTab === "cvOrigine" && (
                            <OriginalResumePdfPreview
                                currentUser={ currentUser }
                                user={ filteredUser }
                                users={ filteredUsersSecondTable }
                            >
                                <p className="text-white" data-tour="step2-manager">
                                    Choisissez un candidat
                                </p>
                                <UserSearch
                                    onSearch={ handleUserSearch } // mise à jour de l'état de recherche
                                    activeSuggestionIndex={ activeSuggestionIndex }
                                    updateActiveSuggestionIndex={
                                        updateActiveSuggestionIndex
                                    }
                                    suggestionsCount={ suggestions.length }
                                    onSelection={ handleSelection } // gestion de la sélection de suggestion
                                    suggestions={ suggestions } // suggestions à afficher
                                />
                                {/* Affichage conditionnel de la boîte de suggestions */ }
                                { ( suggestions.length > 0 || search.length > 0 ) && (
                                    <div className="suggestions-box">
                                        {/* Affichage conditionnel si aucune suggestion n'est disponible */ }
                                        { suggestions.length === 0 ? (
                                            <div className="suggestion">Aucun Résultat</div>
                                        ) : (
                                            // Mapping des suggestions pour affichage
                                            suggestions.map( ( user, idx ) => (
                                                <div
                                                    key={ user.id } // Utilisez id pour la clé si disponible
                                                    className={ `suggestion ${ activeSuggestionIndex === idx
                                                        ? "active"
                                                        : ""
                                                    }` }
                                                    onClick={ () => handleSuggestionClick( user ) }
                                                    onMouseEnter={ () =>
                                                        setActiveSuggestionIndex( idx )
                                                    }
                                                >
                                                    <strong>{ user.nom.toUpperCase() }</strong>
                                                    { ` ${ user.prenom
                                                        .charAt( 0 )
                                                        .toUpperCase() }${ user.prenom
                                                        .slice( 1 )
                                                        .toLowerCase() }` }
                                                </div>
                                            ) )
                                        ) }
                                    </div>
                                ) }
                            </OriginalResumePdfPreview>
                        ) }
                        { activeTab === "testsTechniques" && (
                            <div>
                                <div
                                    className="shadow-4 rounded-5 table-container"
                                    style={ { overflowX: "auto" } }
                                >
                                    <MDBTable>
                                        <MDBTableHead
                                            className={ `${ isDarkMode ? "bg-dark-mode" : "" }` }
                                        >
                                            <tr style={ { color: "#09B4BF" } }>
                                                <th>Nom et prénom</th>
                                                <th>Métier</th>
                                                <th>Résultat</th>
                                            </tr>
                                        </MDBTableHead>
                                        <MDBTableBody style={ { verticalAlign: "middle" } }>
                                            { sortedFilteredUsers.map( ( user ) => (
                                                <tr
                                                    key={ user.id }
                                                    className={ isDarkMode ? "text-white" : "text-muted" }
                                                >
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <img
                                                                src="./spinner-alcyone.png"
                                                                alt=""
                                                                style={ { width: "45px", height: "45px" } }
                                                                className="rounded-circle"
                                                            />
                                                            <p className="fw-bold mb-1 ms-2">
                                                                <span
                                                                    className="text-uppercase">{ user.nom }</span>{ " " }
                                                                { formatPrenom( user.prenom ) }
                                                            </p>
                                                        </div>
                                                    </td>
                                                    <td className="">IT</td>
                                                    <td className="">09/10</td>
                                                </tr>
                                            ) ) }
                                        </MDBTableBody>
                                    </MDBTable>
                                </div>
                            </div>
                        ) }
                    </MDBContainer>
                </section>
            </MDBContainer>
        </MDBContainer>
    );
}
